import { useState } from 'react';

import { useCreateList, useListTemplates } from '../../hooks/useCrm';
import type { CrmListTemplate } from '../../types';

import styles from './crm.module.css';

type ScratchType = 'opportunity' | 'person' | 'organization';

const SCRATCH_TYPES: { key: ScratchType; label: string; blurb: string }[] = [
  { key: 'opportunity', label: 'Opportunities', blurb: 'Each row is a deal (the IOT shape).' },
  { key: 'person', label: 'People', blurb: 'Each row links to a person in the directory.' },
  { key: 'organization', label: 'Organizations', blurb: 'Each row links to an organization.' },
];

const errMsg = (e: unknown, fallback: string) => {
  const d = (e as { detail?: { detail?: string } } | null)?.detail?.detail;
  return typeof d === 'string' ? d : fallback;
};

/**
 * L1 list-creation wizard (spec §2) — the Affinity "Create a new list" flow, native:
 *   1. Template or scratch   2. Type (scratch only)   3. Name → create.
 * A template fixes the type + forces its class server-side (Investor Relations → team_ir); scratch
 * picks the type. New lists are PRIVATE to the creator until shared (sharing UI lands in L4).
 */
export function CrmListWizard({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const templates = useListTemplates();
  const createList = useCreateList();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [template, setTemplate] = useState<CrmListTemplate | null>(null);
  const [subjectKind, setSubjectKind] = useState<ScratchType>('opportunity');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pickTemplate = (t: CrmListTemplate) => {
    setTemplate(t);
    setSubjectKind(t.type);
    setName(t.label);
    setStep(3);
  };
  // reset the (template-prefilled) name so a template→Back→scratch path doesn't leak the stale label
  const pickScratch = () => { setTemplate(null); setName(''); setStep(2); };
  const pickType = (t: ScratchType) => { setSubjectKind(t); setStep(3); };

  const submit = () => {
    if (!name.trim()) { setError('Give the list a name.'); return; }
    setError(null);
    const body = template
      ? { name: name.trim(), template_key: template.key }
      : { name: name.trim(), entry_subject_kind: subjectKind === 'opportunity' ? null : subjectKind };
    createList.mutate(body, {
      onSuccess: (l) => onCreated(l.id),
      onError: (e) => setError(errMsg(e, 'Could not create the list.')),
    });
  };

  return (
    <div className={styles.wizardBackdrop} role="dialog" aria-modal="true" aria-label="Create a new list">
      <div className={styles.wizardPanel}>
        <div className={styles.wizardHeader}>
          <strong>New list</strong>
          {/* a template flow is 2 steps (pick → name); the shared name screen is step 3 in state
              but displays as step 2-of-2, so the counter never reads a nonsensical "3 of 2". */}
          <span className={styles.wizardStep}>Step {template && step === 3 ? 2 : step} of {template ? 2 : 3}</span>
          <button className={styles.gridMiniBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {step === 1 && (
          <div className={styles.wizardBody}>
            <p className={styles.wizardHint}>Start from a template, or build from scratch.</p>
            {templates.isLoading && <p className={styles.wizardHint}>Loading templates…</p>}
            {templates.error != null && (
              <p className={styles.wizardHint}>Couldn’t load templates — you can still build from scratch.</p>
            )}
            <div className={styles.wizardCards}>
              {(templates.data?.templates ?? []).map((t) => (
                <button key={t.key} className={styles.wizardCard} onClick={() => pickTemplate(t)}>
                  <span className={styles.wizardCardTitle}>{t.label}</span>
                  <span className={styles.wizardCardMeta}>
                    {t.type}{t.forces_class ? ` · ${t.forces_class.replace('_', ' ')}` : ''} · {t.fields.length} fields
                  </span>
                  <span className={styles.wizardCardFields}>
                    {t.fields.slice(0, 5).map((f) => f.label).join(', ')}{t.fields.length > 5 ? '…' : ''}
                  </span>
                </button>
              ))}
              <button className={`${styles.wizardCard} ${styles.wizardCardScratch}`} onClick={pickScratch}>
                <span className={styles.wizardCardTitle}>Build from scratch</span>
                <span className={styles.wizardCardMeta}>Choose a type, add your own columns</span>
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={styles.wizardBody}>
            <p className={styles.wizardHint}>What is each row?</p>
            <div className={styles.wizardCards}>
              {SCRATCH_TYPES.map((t) => (
                <button key={t.key} className={styles.wizardCard} onClick={() => pickType(t.key)}>
                  <span className={styles.wizardCardTitle}>{t.label}</span>
                  <span className={styles.wizardCardFields}>{t.blurb}</span>
                </button>
              ))}
            </div>
            <button className={styles.gridPageBtn} onClick={() => setStep(1)}>← Back</button>
          </div>
        )}

        {step === 3 && (
          <div className={styles.wizardBody}>
            <label className={styles.wizardLabel} htmlFor="crm-new-list-name">List name</label>
            <input id="crm-new-list-name" className={styles.wizardInput} value={name} autoFocus
                   onChange={(e) => setName(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
            <p className={styles.wizardHint}>
              {template
                ? `Template: ${template.label} · ${template.type}${template.forces_class ? ` · ${template.forces_class.replace('_', ' ')}` : ''}`
                : `Type: ${subjectKind}`}
              {template?.forces_class === 'team_ir' && ' — LP data, restricted to the IR team.'}
            </p>
            <p className={styles.wizardHint}>Private to you until you share it.</p>
            {error && <div className={styles.gridEditError} role="alert">{error}</div>}
            <div className={styles.wizardActions}>
              <button className={styles.gridPageBtn} onClick={() => setStep(template ? 1 : 2)}>← Back</button>
              <button className={styles.newListBtn} onClick={submit} disabled={createList.isPending}>
                {createList.isPending ? 'Creating…' : 'Create list'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
